import React from 'react'
import { Radio, Form } from 'antd'
import { WrappedFieldProps } from 'redux-form'
import { InputProps } from 'antd/lib/input'
import { FormItemLabelProps } from 'antd/lib/form/FormItemLabel'
import { useTranslation } from 'react-i18next'

// utils
import { FIELD_SIZE, ITEM_LABEL_POSITION, RADIO_BTN_GROUP_FIELD, TEXT_TYPES, TEXT_ALIGNMENT, RADIO_BUTTON_GROUP_ALIGNMENT } from '../utils/enums'

// assets
import { ReactComponent as UnderlineIcon } from '../assets/icons/BaseIcons/underline-1-icon.svg'
import { ReactComponent as ItalicIcon } from '../assets/icons/BaseIcons/italic-1-icon.svg'
import { ReactComponent as BoldIcon } from '../assets/icons/BaseIcons/bold-1-icon.svg'
import { ReactComponent as AlignLeft } from '../assets/icons/BaseIcons/align-left-1.svg'
import { ReactComponent as AlignRight } from '../assets/icons/BaseIcons/align-right-1.svg'
import { ReactComponent as AlignCenter } from '../assets/icons/BaseIcons/align-center-1.svg'
import { ReactComponent as AlignVertical } from '../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as AlignHorizontal } from '../assets/icons/BaseMiniIcons/width-icon.svg'

const { Item } = Form

type Props = WrappedFieldProps &
	InputProps &
	FormItemLabelProps & {
		disabled: boolean
		optionsType: string
	}

const TypeRadioGroupField = (props: Props) => {
	const { t } = useTranslation()
	const {
		input,
		label,
		required,
		meta: { error, touched },
		style,
		disabled,
		optionsType,
		size
	} = props

	const deselectRadioButton = (value: string) => {
		// deselecting radio button when user click twice on same option
		if (input.value === value) {
			input.onChange('')
		}
	}

	// render options based on options type
	const renderOptions = (type: string) => {
		switch (type) {
			case RADIO_BTN_GROUP_FIELD.TEXT_TYPE:
				return (
					<>
						<Radio.Button
							key={TEXT_TYPES.BOLD}
							value={TEXT_TYPES.BOLD}
							onClick={() => deselectRadioButton(TEXT_TYPES.BOLD)}
							className={'radio-group-btn-left'}
						>
							<BoldIcon />
						</Radio.Button>
						<Radio.Button key={TEXT_TYPES.ITALIC} value={TEXT_TYPES.ITALIC} onClick={() => deselectRadioButton(TEXT_TYPES.ITALIC)}>
							<ItalicIcon />
						</Radio.Button>
						<Radio.Button
							key={TEXT_TYPES.UNDERLINE}
							value={TEXT_TYPES.UNDERLINE}
							onClick={() => deselectRadioButton(TEXT_TYPES.UNDERLINE)}
							className={'radio-group-btn-right'}
						>
							<UnderlineIcon />
						</Radio.Button>
					</>
				)
			case RADIO_BTN_GROUP_FIELD.POSITION:
				return (
					<>
						<Radio.Button key={ITEM_LABEL_POSITION.LEFT} value={ITEM_LABEL_POSITION.LEFT} className={'radio-group-btn-left'}>
							{t('loc: Left')}
						</Radio.Button>
						<Radio.Button key={ITEM_LABEL_POSITION.TOP} value={ITEM_LABEL_POSITION.TOP} className={'radio-group-btn-right'}>
							{t('loc: Top')}
						</Radio.Button>
					</>
				)
			case RADIO_BTN_GROUP_FIELD.SIZE:
				return (
					<>
						<Radio.Button key={FIELD_SIZE.S} value={FIELD_SIZE.S} className={'radio-group-btn-left'}>
							{t('loc:S')}
						</Radio.Button>
						<Radio.Button key={FIELD_SIZE.M} value={FIELD_SIZE.M}>
							{t('loc:M')}
						</Radio.Button>
						<Radio.Button key={FIELD_SIZE.L} value={FIELD_SIZE.L} className={'radio-group-btn-right'}>
							{t('loc:L')}
						</Radio.Button>
					</>
				)
			case RADIO_BTN_GROUP_FIELD.TWO_SIZE:
				return (
					<>
						<Radio.Button key={FIELD_SIZE.S} value={FIELD_SIZE.S} className={'radio-group-btn-left'}>
							{t('loc:S')}
						</Radio.Button>
						<Radio.Button key={FIELD_SIZE.M} value={FIELD_SIZE.M} className={'radio-group-btn-right'}>
							{t('loc:M')}
						</Radio.Button>
					</>
				)
			case RADIO_BTN_GROUP_FIELD.TEXT_ALIGN:
				return (
					<>
						<Radio.Button
							key={TEXT_ALIGNMENT.LEFT}
							value={TEXT_ALIGNMENT.LEFT}
							className={'radio-group-btn-left'}
							onClick={() => deselectRadioButton(TEXT_ALIGNMENT.LEFT)}
						>
							<AlignLeft />
						</Radio.Button>
						<Radio.Button
							key={TEXT_ALIGNMENT.CENTER}
							value={TEXT_ALIGNMENT.CENTER}
							className={'radio-group-btn-center'}
							onClick={() => deselectRadioButton(TEXT_ALIGNMENT.CENTER)}
						>
							<AlignCenter />
						</Radio.Button>
						<Radio.Button
							key={TEXT_ALIGNMENT.RIGHT}
							value={TEXT_ALIGNMENT.RIGHT}
							className={'radio-group-btn-right'}
							onClick={() => deselectRadioButton(TEXT_ALIGNMENT.RIGHT)}
						>
							<AlignRight />
						</Radio.Button>
					</>
				)
			case RADIO_BTN_GROUP_FIELD.GROUP_ALIGN:
				return (
					<>
						<Radio.Button
							key={RADIO_BUTTON_GROUP_ALIGNMENT.HORIZONTAL}
							value={RADIO_BUTTON_GROUP_ALIGNMENT.HORIZONTAL}
							className={'radio-group-btn-left'}
							onClick={() => deselectRadioButton(RADIO_BUTTON_GROUP_ALIGNMENT.HORIZONTAL)}
						>
							<AlignHorizontal />
						</Radio.Button>
						<Radio.Button
							key={RADIO_BUTTON_GROUP_ALIGNMENT.VERTICAL}
							value={RADIO_BUTTON_GROUP_ALIGNMENT.VERTICAL}
							className={'radio-group-btn-right'}
							onClick={() => deselectRadioButton(RADIO_BUTTON_GROUP_ALIGNMENT.VERTICAL)}
						>
							<AlignVertical />
						</Radio.Button>
					</>
				)
			default:
				return undefined
		}
	}

	return (
		<Item required={required} label={label} help={touched && error} validateStatus={error && touched ? 'error' : undefined} style={style}>
			<Radio.Group className={'form-type-radio-btn-group w-full'} value={input.value || ''} onChange={input.onChange} size={size} disabled={disabled}>
				{renderOptions(optionsType)}
			</Radio.Group>
		</Item>
	)
}

export default TypeRadioGroupField
